import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { LoadingScreen } from 'components/screens';
import { CVBuilder } from 'modules/cv-builder';
import { CvDto } from 'modules/cv-builder/api-types';
import { useStepNavigate } from 'modules/cv-builder/hooks/useStepNavigate';
import { GuestCvDataProvider } from 'modules/guest-cv/GuestCvDataProvider';
import { useGuestSession } from 'modules/guest-cv/GuestSessionContext';
import { GuestSessionProvider } from 'modules/guest-cv/GuestSessionProvider';
import { GuestStatusScreen } from 'modules/guest-cv/GuestStatusScreen';
import { computeResumeStep } from 'modules/guest-cv/resumeStep';
import { Layout } from 'modules/minimal-ui-theme';

/**
 * On the first active render (arriving from the email link, which has no `?step=`), drop
 * the talent at the right step from the server's acknowledged steps. Runs once; in-session
 * navigation (which adds `?step=`) is never overridden.
 *
 * Crucially, the existing search params — above all the magic-link token `?t=` — are
 * preserved when we add `?step=`. The guest session lives only in memory, so keeping the
 * token in the URL is what lets a page reload re-redeem the still-active grant and resume,
 * instead of dead-ending on `missing-token` (which reads to the applicant like a dead link
 * and forces them to request a brand-new grant). Re-redeeming an active grant is safe: the
 * server just mints a fresh session and never consumes the grant.
 */
const GuestResumeRedirect = ({
  completedSteps,
  cv
}: {
  completedSteps: string[];
  cv: CvDto | null;
}) => {
  const [searchParams] = useSearchParams();
  const stepNavigate = useStepNavigate();
  const redirected = useRef(false);

  useEffect(() => {
    if (redirected.current) {
      return;
    }
    redirected.current = true;
    if (searchParams.get('step')) {
      return;
    }
    // Pass the CV so a re-granted talent with a finished CV lands on the Summary even
    // though this fresh grant's completedSteps is empty (per-grant vs per-talent).
    // stepNavigate keeps the `?t=` token so a reload can re-redeem and resume.
    stepNavigate(computeResumeStep(completedSteps, cv), { replace: true });
  }, [completedSteps, cv, stepNavigate, searchParams]);

  return null;
};

const GuestCvFlow = () => {
  const theme = useTheme();
  const { redeemState, redeemData } = useGuestSession();

  if (redeemState === 'loading') {
    return <LoadingScreen />;
  }

  if (redeemState !== 'active') {
    return <GuestStatusScreen state={redeemState} />;
  }

  return (
    <GuestCvDataProvider>
      <GuestResumeRedirect
        completedSteps={redeemData?.completedSteps ?? []}
        cv={redeemData?.cv ?? null}
      />
      <Layout
        backgroundColor={theme.palette.background.neutral}
        childrenStyles={{ pb: 0, height: '100%' }}
      >
        {/* Mask the applicant's CV (name/email/education/…) in Datadog session replay —
            'mask-user-input' covers typed inputs but NOT rendered text (e.g. the Summary).
            Scoped here so it doesn't change global telemetry for the rest of the app. */}
        <div data-dd-privacy="mask" style={{ height: '100%' }}>
          <CVBuilder />
        </div>
      </Layout>
    </GuestCvDataProvider>
  );
};

/**
 * Public, Auth0-free page for the magic-link CV builder. Mounted OUTSIDE
 * Auth0ProviderWithNavigate, so it never triggers a login redirect. The link token
 * rides in `?t=`; we set `Referrer-Policy: no-referrer` while this page is mounted so
 * the token can't leak via the Referer header to third-party assets.
 */
export const GuestCvBuilderPage = () => {
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'referrer';
    meta.content = 'no-referrer';
    document.head.appendChild(meta);
    return () => {
      // remove() instead of head.removeChild(meta): translation extensions and in-app
      // browsers rewrite <head> behind React's back, and removeChild throws NotFoundError
      // once the node has been detached or re-parented. remove() is a no-op in that case.
      meta.remove();
    };
  }, []);

  return (
    <GuestSessionProvider>
      <GuestCvFlow />
    </GuestSessionProvider>
  );
};
